@let isEC2 = restoreAs === restoreAsDiskEnum.EC2;
@let isAzure = restoreAs === restoreAsDiskEnum.AVM;

<section [formGroup]="stepForm">
  <mbs-form-group class="mb-3">
    <div class="mbs-form_label">
      {{ 'wizards:partitions_subtitle' | i18next }}
      <span class="mbs-form_label-required">*</span>
    </div>
    <mbs-list-select
      id="partition-step-table"
      listType="table"
      [bindDisabledValues]="disabledRowBy"
      [bindSelected]="bindSelected"
      [data]="partitions"
      [headers]="headers"
      [invalid]="needValidate && stepForm.touched && !value?.valid"
      [keepState]="true"
      [loadSource]="false"
      [maxHeight]="'200px'"
      [selectable]="false"
      [selectedData]="selectedItems"
      [showCheckboxes]="true"
      [showHeaders]="true"
      [showSearch]="false"
      (changeSelected)="itemsCheckedHandler($event)">
      <ng-template
        let-disk
        mbsTableCell>
        <mbs-text-ellipsis [tooltip]="disk.diskNumber || '-'">
          {{ disk.diskNumber || '-' }}
        </mbs-text-ellipsis>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        {{ disk.mountPointsInfo }}
        <ng-container
          *ngIf="isEdit && (disk[disabledRowBy.key] === disabledRowBy.value || disk[disabledRowBy.key] === +VolumeUsageStateEnum.New)">
          <span
            class="fa fa-info-circle ml-2 cursor-help text-info fw-bold"
            container="body"
            [ngbTooltip]="
              disk[disabledRowBy.key] === disabledRowBy.value
                ? ('wizards:partitions_volume_missing_text' | i18next)
                : ('wizards:partitions_volume_detected_text' | i18next)
            "></span>
        </ng-container>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        {{ disk.lengthString }}
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        <mbs-button
          *ngIf="disk.fileSystemType === fileSystemTypeEnum[fileSystemTypeEnum.NTFS]; else onlyUsed"
          type="primary"
          [customClasses]="getUsedSpaceStringClass(disk)"
          [isCtrl]="true"
          (click)="usedSpaceClickHandler(disk)">
          {{ disk.usedSpaceString }}
          <span class="fa fa-folder-open ml-1"></span>
        </mbs-button>
        <ng-template #onlyUsed>
          {{ disk.usedSpaceString }}
        </ng-template>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        <mbs-button
          *ngIf="disk.fileSystemType === fileSystemTypeEnum[fileSystemTypeEnum.NTFS]; else onlySize"
          type="primary"
          [isCtrl]="true"
          (click)="targetSizeClickHandler(disk, targetSizeModal)">
          {{ getDiskSizeFromMB(disk.targetSize) || 0 }}
          <span class="fa fa-pencil ml-1"></span>
        </mbs-button>
        <ng-template #onlySize>
          {{ getDiskSizeFromMB(disk.targetSize) || 0 }}
        </ng-template>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        <mbs-text-ellipsis [tooltip]="disk.label">
          {{ disk.label || '&mdash;' }}
        </mbs-text-ellipsis>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        <mbs-text-ellipsis [tooltip]="disk.fileSystemType">
          {{ disk.fileSystemType }}
        </mbs-text-ellipsis>
      </ng-template>
      <ng-template
        let-disk
        mbsTableCell>
        {{ getSectorSize(disk.sectorSize) }}
      </ng-template>
    </mbs-list-select>
  </mbs-form-group>

  <div class="form-row align-items-center mb-2">
    <div class="col-6">
      <mbs-checkbox
        formControlName="convertToMBR"
        [id]="'restore-convertGPTToBios-checkbox'"
        [label]="'wizards:partitions_convert_gpt_label' | i18next"
        (change)="convertToMBRChangeHandler($event)">
        <ng-container appendLabel>
          <span
            class="fa fa-info-circle ml-2 cursor-help text-info"
            [ngbTooltip]="'wizards:partitions_tooltip_text' | i18next"></span>
        </ng-container>
      </mbs-checkbox>
    </div>

    @if (isUEFI && isEC2) {
      <div class="col-6 d-flex align-items-center justify-content-end">
        <mbs-input-label
          [label]="'mbs-ui:cloud-restore.selectPartitionsStep.bootMode' | i18next"
          [labelClasses]="'mb-0 mr-2'"></mbs-input-label>
        <mbs-select
          formControlName="bootMode"
          style="width: 210px"
          [appendTo]="'.modal-content'"
          [bindLabel]="'label'"
          [bindValue]="'value'"
          [clearable]="false"
          [id]="'restore-boot-mode-select'"
          [items]="bootModes"></mbs-select>
      </div>
    }
  </div>

  @if (isUEFI && isEC2) {
    <mbs-alert
      [icon]="true"
      [type]="mbsPopupType.warning">
      <div>{{ 'mbs-ui:cloud-restore.selectPartitionsStep.notSupportAbilitiesAlert' | i18next }}</div>

      @if (isEC2) {
        <div>
          {{ 'mbs-ui:cloud-restore.selectPartitionsStep.referToDocumentation' | i18next }}
          <a
            rel="noopener noreferrer"
            target="_blank"
            [href]="'https://aws.amazon.com/blogs/aws/amazon-ec2-now-supports-nitrotpm-and-uefi-secure-boot'">
            {{ 'mbs-ui:cloud-restore.selectPartitionsStep.article1' | i18next }} </a
          >,
          <a
            rel="noopener noreferrer"
            target="_blank"
            [href]="'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ami-boot.html#boot-considerations'">
            {{ 'mbs-ui:cloud-restore.selectPartitionsStep.article2' | i18next }}
          </a>
        </div>
      }

      @if (isAzure) {
        <div>
          <span>{{ 'mbs-ui:cloud-restore.selectPartitionsStep.referTo' | i18next }} </span>
          <a
            rel="noopener noreferrer"
            target="_blank"
            [href]="'https://learn.microsoft.com/en-us/azure/virtual-machines/generation-2'">
            {{ 'mbs-ui:cloud-restore.selectPartitionsStep.azureDocumentation' | i18next }}
          </a>
        </div>
      }
    </mbs-alert>

    @if (isEC2) {
      <mbs-alert
        [icon]="true"
        [type]="mbsPopupType.warning">
        {{ 'mbs-ui:cloud-restore.selectPartitionsStep.secureBootAlert' | i18next }}
        <a
          rel="noopener noreferrer"
          target="_blank"
          [href]="'https://docs.aws.amazon.com/AWSEC2/latest/WindowsGuide/launch-instance-with-uefi-sb.html'">
          {{ 'shared:buttons.learnMore' | i18next }}
        </a>
      </mbs-alert>
    }
  }

  @if ((isEC2 || isAzure) && notSelectedSystemRequired) {
    <mbs-alert
      [icon]="true"
      [type]="mbsPopupType.warning">
      {{ 'mbs-ui:cloud-restore.selectPartitionsStep.rootPartitionAlert' | i18next: { name: isEC2 ? 'Amazon EC2' : 'MS Azure' } }}
    </mbs-alert>
  }
</section>

<ng-template #targetSizeModal>
  <div
    class="mbs-form_content"
    [formGroup]="sizeForm">
    <mbs-form-group>
      {{ 'wizards:destinationOld.dest_minimum_label' | i18next }} {{ sizeForm.get('min').value }} {{ sizeForm.get('sizeFormat').value }}
    </mbs-form-group>
    <mbs-form-group>
      {{ 'wizards:destinationOld.dest_maximum_label' | i18next }} {{ sizeForm.get('max').value }} {{ sizeForm.get('sizeFormat').value }}
    </mbs-form-group>
    <mbs-form-group>
      <div class="form-row align-items-center">
        <div class="col-auto">
          <label
            class="mbs-form_label mb-0"
            [for]="'size-number'">
            {{ 'wizards:partitions_size_label' | i18next }}
          </label>
        </div>
        <div class="col-3">
          <mbs-number
            formControlName="size"
            [id]="'size-number'"
            [max]="sizeForm.get('max').value || 65536.0"
            [min]="numberIsFocused ? 0 : sizeForm.get('min').value || 0.02"
            (blur)="capacityBlurHandler()"
            (change)="sizeChangeHandler($event)"
            (focus)="capacityFocusHandler()"></mbs-number>
        </div>
        <div class="col-3">
          <mbs-select
            formControlName="sizeFormat"
            [clearable]="false"
            [items]="sizeFormats"
            [searchable]="false"
            (change)="sizeFormatChangeHandler($event)"></mbs-select>
        </div>
      </div>
    </mbs-form-group>

    <mbs-alert
      class="mb-0 mt-3"
      [icon]="true"
      [type]="mbsPopupType.warning">
      {{ 'wizards:partitions_size_modal_alert_text' | i18next }}
    </mbs-alert>
  </div>
</ng-template>

<ng-template #systemRequiredModalContent>
  <p>{{ 'wizards:system_required_modal_text' | i18next }}</p>
  <mbs-alert
    class="mb-0"
    [icon]="true"
    [type]="mbsPopupType.warning">
    {{ 'wizards:system_required_modal_alert_text' | i18next }}
  </mbs-alert>
</ng-template>
